import React, { useState, useCallback, useId, useEffect } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { FormField } from 'site-modules/shared/components/form-field/form-field';
import { FieldError } from 'client/site-modules/shared/components/field-error/field-error';
import { ZIP_CODE_INPUT } from 'client/site-modules/shared/utils/appraiser/appraiser-validation-helper';
import { useDebounce } from 'site-modules/shared/hooks/use-debounce';

import './zip-field.scss';

export const THEME = {
  LOCATION_ICON: 'location-icon',
};

const MAX_LENGTH = 8;

export const ZipField = ({
  id = '',
  zipCode: zipCodeFromProps = '',
  isZipCodeValid,
  onZipCodeChange,
  errorMap = new Map(),
  disabled = false,
  isMobile = false,
  inputClassName = '',
  zipWrapperClassName = '',
  theme = undefined,
  zipClassName = '',
  labelClassName = 'small mb-0_25',
}) => {
  const [touched, setIsTouched] = useState(false);
  const [isEmptyZipCode] = useState(!zipCodeFromProps);
  const [zipCode, setZipCode] = useState(zipCodeFromProps);
  const debouncedOnZipCodeChange = useDebounce(onZipCodeChange, 250);

  useEffect(() => {
    // Intentional synchronization of the controlled ZIP input value.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setZipCode(zipCodeFromProps);
  }, [zipCodeFromProps]);

  const onComponentZipCodeChangeChange = useCallback(
    e => {
      const value = e.target.value.slice(0, MAX_LENGTH);
      setZipCode(value);
      debouncedOnZipCodeChange(value);
    },
    [debouncedOnZipCodeChange]
  );

  const onBlur = useCallback(() => {
    setIsTouched(true);
  }, []);

  const getZipFormFieldThemeProps = useCallback(() => {
    switch (theme) {
      case THEME.LOCATION_ICON:
        return {
          className: classNames('zip-field-wrapper', 'location-icon-theme', zipClassName),
          labelClassName: classNames('icon-location text-primary-darker size-16', labelClassName),
          label: ' ', // ' ' is required for pin icon
        };
      default:
        return {
          className: classNames('zip-field-wrapper', zipClassName),
          label: 'Zip',
          labelClassName,
        };
    }
  }, [labelClassName, theme, zipClassName]);

  const errorZipCode = errorMap.get(ZIP_CODE_INPUT);
  const uniqId = useId();
  const errorId = `${ZIP_CODE_INPUT}-error-${uniqId}`;
  const zipId = id || `zip-${uniqId}`;
  const isValid = !errorZipCode && (isZipCodeValid || (!isZipCodeValid && isEmptyZipCode && !touched));

  return (
    <div className={classNames(zipWrapperClassName)}>
      <FormField
        type="number"
        inputMode="numeric"
        pattern="[0-9]*"
        id={zipId}
        name="zip"
        aria-label="zip code"
        inputClassName={classNames('value-zip-input', inputClassName)}
        value={zipCode}
        placeholder={isMobile ? 'Zip' : 'Enter Zip Code'}
        isValid={isValid}
        ariaInvalid={!isValid}
        onChange={onComponentZipCodeChangeChange}
        onBlur={onBlur}
        disabled={disabled}
        {...getZipFormFieldThemeProps()}
        {...(errorZipCode ? { ariaDescribedBy: errorId } : {})}
        aria-required
        required
        isLiveRegion
        autoComplete="postal-code"
      />
      <FieldError id={errorId} error={errorZipCode} isLiveRegion classes="mt-0_5 zip-error-message-desktop" />
    </div>
  );
};

ZipField.propTypes = {
  id: PropTypes.string,
  isZipCodeValid: PropTypes.bool.isRequired,
  onZipCodeChange: PropTypes.func.isRequired,
  zipCode: PropTypes.string,
  errorMap: PropTypes.objectOf(PropTypes.string),
  zipClassName: PropTypes.string,
  labelClassName: PropTypes.string,
  disabled: PropTypes.bool,
  isMobile: PropTypes.bool,
  inputClassName: PropTypes.string,
  zipWrapperClassName: PropTypes.string,
  theme: PropTypes.string,
};
